import { Box } from '@mui/material';
import type { FC } from 'react';
import { useCallback } from 'react';
import type { BrazeBanner } from '~/lib/braze.client';
import { getBannerPromoData, getCurrentBanner, insertBannerIntoContainer } from '~/lib/braze.client';
import { getLogger } from '~/lib/logger/logger';
import { trackPromoEvent } from '~/utils/gtmUtils';
import { markPromoViewed } from '~/utils/promoViews';

const logger = getLogger('BrazeBannerPlacement');

const PROMO_VIEW_VISIBILITY_THRESHOLD = 0.5;
const PROMO_VIEW_MIN_DWELL_MS = 1000;

const warnedUnconfiguredPromoKeys = new Set<string>();

// Otherwise a campaign CRM forgot to configure only shows up weeks later as a gap in GA.
const warnUnconfiguredPromo = (placementId: string, bannerId: string) => {
  const promoKey = `${placementId}:${bannerId}`;
  if (warnedUnconfiguredPromoKeys.has(promoKey)) {
    return;
  }
  warnedUnconfiguredPromoKeys.add(promoKey);
  logger.warn('Braze banner has no promo tracking properties, so GA promo events are skipped', {
    placementId,
    bannerId,
  });
};

/**
 * Reads promo data from whichever banner Braze holds *now* rather than the one inserted at
 * mount - `insertBanner` can render a cached placeholder first and swap in the real banner
 * later, and the placeholder may have stale or missing promo properties.
 */
const getLatestPromo = (placementId: string, insertedBanner: BrazeBanner) => {
  const banner = getCurrentBanner(placementId) ?? insertedBanner;
  return { banner, promoData: getBannerPromoData(banner) };
};

const trackBannerView = (
  node: HTMLElement,
  placementId: string,
  insertedBanner: BrazeBanner | null | undefined,
): (() => void) => {
  if (!insertedBanner || insertedBanner.isControl || typeof IntersectionObserver === 'undefined') {
    return () => {};
  }

  const recordView = () => {
    const { banner, promoData } = getLatestPromo(placementId, insertedBanner);
    if (!promoData) {
      warnUnconfiguredPromo(placementId, banner.id);
      return;
    }

    if (markPromoViewed(`${placementId}:${banner.id}`)) {
      trackPromoEvent('promo_view', promoData);
    }
  };

  let isVisibleEnough = false;
  let dwellTimer: ReturnType<typeof setTimeout> | undefined;

  const cancelDwell = () => {
    clearTimeout(dwellTimer);
    dwellTimer = undefined;
  };

  // A view only counts once the banner has stayed visible enough, on the active tab, for the
  // whole dwell time - leaving the viewport or the tab restarts the clock.
  const startDwell = () => {
    if (dwellTimer !== undefined || !isVisibleEnough || document.visibilityState !== 'visible') {
      return;
    }
    dwellTimer = setTimeout(() => {
      dwellTimer = undefined;
      recordView();
    }, PROMO_VIEW_MIN_DWELL_MS);
  };

  // Not disconnected after the first view - the banner may not have promo data yet on its first
  // view, and `markPromoViewed` already stops repeat views within a page view.
  const observer = new IntersectionObserver(
    ([entry]) => {
      // `isIntersecting` stays true for any partial overlap, including when the banner drops back
      // below the threshold - the ratio is what says whether enough of it is actually on screen.
      isVisibleEnough = !!entry?.isIntersecting && entry.intersectionRatio >= PROMO_VIEW_VISIBILITY_THRESHOLD;
      if (isVisibleEnough) {
        startDwell();
      } else {
        cancelDwell();
      }
    },
    { threshold: PROMO_VIEW_VISIBILITY_THRESHOLD },
  );
  observer.observe(node);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      startDwell();
    } else {
      cancelDwell();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    observer.disconnect();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    cancelDwell();
  };
};

export type BrazeBannerPlacementProps = {
  placementId: string;
};

/**
 * Renders a single Braze Banner placement into a container Braze owns the
 * HTML of. A/B holdout control banners are still inserted (so Braze can
 * track the impression) but hidden from the visitor.
 */
export const BrazeBannerPlacement: FC<BrazeBannerPlacementProps> = ({ placementId }) => {
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return;
      }

      const handleBannerLinkClick = (clickedBanner: BrazeBanner) => {
        const { promoData } = getLatestPromo(placementId, clickedBanner);
        if (promoData) {
          trackPromoEvent('promo_click', promoData);
        }
      };

      const { banner, cleanup } = insertBannerIntoContainer(placementId, node, handleBannerLinkClick);
      if (banner?.isControl) {
        node.style.display = 'none';
      }

      const stopTrackingView = trackBannerView(node, placementId, banner);

      return () => {
        stopTrackingView();
        cleanup();
      };
    },
    [placementId],
  );

  return (
    <Box
      ref={containerRef}
      data-testid={`braze-banner-${placementId}`}
      sx={{
        width: '100%',
        height: '100%',
        // Braze's banner HTML sizes its own root element inline - force it to fill
        // this container (which defines the carousel's actual dimensions) regardless.
        // Braze renders real (non-control) banners as an <iframe>, which defaults to
        // display: inline like an <img> - left as inline, it sits on a text baseline
        // and leaves a few px of line-height gap below it. Forcing display: block
        // removes that gap so it fills the container edge-to-edge.
        '& > *': {
          display: 'block !important',
          width: '100% !important',
          height: '100% !important',
        },
        '& img': {
          objectFit: 'cover',
        },
      }}
    />
  );
};
